import { useMemo, useState } from 'react';
import type { AiPreviewScreen } from '@/features/campaigns/types/campaign';
import { aiScreenKey } from './aiPriceEstimate';
import type { AiPreviewOrientation } from './AIPreview.types';

/**
 * Local overrides layered over the AI plan: which screens stay in, plays per day for each, and
 * the chosen orientations (one or both). Anything the user has not touched follows the plan.
 */
export function useAiPlanCustomisation(
  screens: AiPreviewScreen[],
  planOrientations: AiPreviewOrientation[],
  planPlays: Record<string, number>,
  pendingSave = false,
) {
  const aiKeys = useMemo(() => screens.map(aiScreenKey).filter(Boolean), [screens]);

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set(aiKeys));
  const [orientations, setOrientations] = useState<AiPreviewOrientation[]>(planOrientations);
  const [playsOverride, setPlaysOverride] = useState<Record<string, number>>({});

  // A new plan (re-plan for orientation or coupon, or a saved selection) starts from itself again.
  // The exception is a screen the user just added: the plan then only grew, so everything they
  // already unticked or changed stays exactly as they left it.
  const planSig = `${aiKeys.join('|')}#${planOrientations.join(',')}#${JSON.stringify(planPlays)}`;
  const [appliedSig, setAppliedSig] = useState(planSig);
  const [knownKeys, setKnownKeys] = useState(aiKeys);
  if (appliedSig !== planSig) {
    setAppliedSig(planSig);
    setKnownKeys(aiKeys);
    if (pendingSave) {
      setSelectedKeys((prev) => new Set(aiKeys.filter((key) => prev.has(key) || !knownKeys.includes(key))));
    } else {
      setSelectedKeys(new Set(aiKeys));
      setOrientations(planOrientations);
      setPlaysOverride({});
    }
  }

  const playsByKey = useMemo(
    () => Object.fromEntries(aiKeys.map((key) => [key, playsOverride[key] ?? planPlays[key] ?? 0])),
    [aiKeys, playsOverride, planPlays],
  );

  const toggleScreen = (key: string) =>
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const setPlays = (key: string, plays: number) =>
    setPlaysOverride((prev) => ({ ...prev, [key]: Math.max(1, Math.round(plays)) }));

  const reset = () => {
    setSelectedKeys(new Set(aiKeys));
    setOrientations(planOrientations);
    setPlaysOverride({});
  };

  const selectionChanged =
    selectedKeys.size !== aiKeys.length || aiKeys.some((k) => !selectedKeys.has(k));
  const playsChanged = aiKeys.some(
    (k) => selectedKeys.has(k) && playsByKey[k] !== (planPlays[k] ?? 0),
  );
  const orientationsChanged = orientations.join(',') !== planOrientations.join(',');
  const isCustomised = orientationsChanged || selectionChanged || playsChanged;

  return {
    selectedKeys,
    toggleScreen,
    playsByKey,
    setPlays,
    orientations,
    setOrientations,
    orientationsChanged,
    isCustomised,
    /** Screens or plays changed, i.e. the saved booking needs updating. */
    needsSave: selectionChanged || playsChanged || pendingSave,
    reset,
  };
}
